import { SCREENSHOT_BLOCKING_SELECTOR } from "./screenshot-ignore.const";

/**
 * The user dismissed the browser's share picker. Not a failure — it needs its
 * own type so the dialog can say "cancelled" rather than "something broke".
 */
export class ScreenshotCancelledError extends Error {
  constructor(message = "The tab capture was not allowed") {
    super(message);
    this.name = "ScreenshotCancelledError";
  }
}

export const isScreenshotSupported = (): boolean =>
  typeof navigator !== "undefined" &&
  typeof navigator.mediaDevices?.getDisplayMedia === "function";

/**
 * How long to keep waiting for launcher chrome to unmount before grabbing
 * anyway. The exit transition is 195ms and the user's click on the share picker
 * costs far more than that, so this ceiling should never be reached; it is here
 * so a stuck element cannot hang the capture forever.
 */
export const BLOCKING_TIMEOUT_MS = 1500;

/** A tab that produces no frame in this long is not going to. */
export const FRAME_TIMEOUT_MS = 5000;

/**
 * Frames to let pass before grabbing. The first frame of a fresh capture track
 * is routinely the pre-capture composite, so taking it can catch the launcher
 * mid-fade even after the DOM says it is gone.
 */
const FRAMES_TO_SETTLE = 2;

const nextFrame = (video: HTMLVideoElement): Promise<void> =>
  new Promise((resolve) => {
    // `requestVideoFrameCallback` resolves on an actual decoded frame;
    // `requestAnimationFrame` only says the page painted. Prefer the former and
    // fall back where it does not exist.
    const request = (
      video as HTMLVideoElement & {
        requestVideoFrameCallback?: (callback: () => void) => number;
      }
    ).requestVideoFrameCallback;
    if (typeof request === "function") {
      request.call(video, () => resolve());
      return;
    }
    window.requestAnimationFrame(() => resolve());
  });

const withTimeout = <T>(
  promise: Promise<T>,
  ms: number,
  message: string
): Promise<T> =>
  Promise.race([
    promise,
    new Promise<T>((_, reject) => {
      window.setTimeout(() => reject(new Error(message)), ms);
    })
  ]);

/**
 * Blocks until no launcher chrome is painted. See
 * `SCREENSHOT_BLOCKING_ATTRIBUTE` — with a pixel capture there is nothing to
 * filter, so waiting is the whole mechanism.
 */
const waitForBlockingElements = async (): Promise<void> => {
  const deadline = Date.now() + BLOCKING_TIMEOUT_MS;
  while (document.querySelector(SCREENSHOT_BLOCKING_SELECTOR)) {
    if (Date.now() > deadline) return;
    // eslint-disable-next-line no-await-in-loop
    await new Promise((resolve) => {
      window.requestAnimationFrame(() => resolve(null));
    });
  }
};

const grabFrame = async (stream: MediaStream): Promise<HTMLCanvasElement> => {
  const video = document.createElement("video");
  video.srcObject = stream;
  video.muted = true;
  video.playsInline = true;
  // Parked off-screen rather than left detached: a detached <video> is not
  // guaranteed to be driven by the compositor, so frame callbacks may never
  // fire. Far off the left edge keeps it out of its own capture.
  video.style.cssText =
    "position:fixed;top:-10000px;left:-10000px;width:1px;height:1px;opacity:0;pointer-events:none";
  document.body.appendChild(video);

  try {
    await video.play();
    await withTimeout(
      (async () => {
        for (let i = 0; i < FRAMES_TO_SETTLE; i += 1) {
          // eslint-disable-next-line no-await-in-loop
          await nextFrame(video);
        }
      })(),
      FRAME_TIMEOUT_MS,
      "Timed out waiting for a frame from the tab"
    );

    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    if (!canvas.width || !canvas.height) {
      throw new Error("The tab capture produced an empty frame");
    }
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("Canvas 2D context unavailable");
    ctx.drawImage(video, 0, 0);
    return canvas;
  } finally {
    video.pause();
    video.srcObject = null;
    video.remove();
  }
};

/**
 * The visible tab, as a canvas — the browser's own composited pixels.
 *
 * This replaced an `html-to-image` capture, and the reason is worth keeping:
 * that library does not photograph the page, it **clones the DOM into an SVG
 * `<foreignObject>` and re-renders it**. A second render with different inputs
 * drifts, and three of those drifts were visible in practice — text reflowed
 * because web fonts were not embedded (and `cloneCSSStyle` shaves 0.1px off
 * every `font-size` as a Firefox workaround), every nested scroll container
 * restarted at its top because the library has no scroll handling at all
 * (`grep scrollTop node_modules/html-to-image/src` finds nothing), and
 * `position: fixed` / `100vh` resolved against the foreignObject instead of the
 * viewport, so fixed chrome overlapped. For a tool whose job is illustrating a
 * layout bug, a renderer that invents its own layout bugs is worse than
 * useless. `getDisplayMedia` has none of these failure modes by construction —
 * it is the compositor's output, fonts, scroll offsets, canvas content and all.
 *
 * The cost is a share-picker click per capture, which Chrome will not persist.
 *
 * Must be called while the page still has transient user activation (i.e. in
 * the same 5s window as the keystroke or click that asked for a screenshot), so
 * do not put a timer in front of it — the wait for launcher chrome happens
 * *after* the picker, by which point the user's own click has covered it.
 */
export default async function capturePage(): Promise<HTMLCanvasElement> {
  if (!isScreenshotSupported()) {
    throw new Error("This browser cannot capture the current tab");
  }

  let stream: MediaStream;
  try {
    stream = await navigator.mediaDevices.getDisplayMedia({
      audio: false,
      video: {
        displaySurface: "browser",
        // Ask for the tab at its real device resolution; without this Chrome
        // caps tab capture well below a retina viewport.
        width: {
          ideal: Math.round(window.innerWidth * (window.devicePixelRatio || 1))
        },
        height: {
          ideal: Math.round(window.innerHeight * (window.devicePixelRatio || 1))
        }
      },
      // `preferCurrentTab` puts this tab at the top of the picker;
      // `selfBrowserSurface: "include"` is what makes capturing ourselves legal
      // at all. Neither is in lib.dom yet, hence the cast.
      preferCurrentTab: true,
      selfBrowserSurface: "include",
      surfaceSwitching: "exclude",
      systemAudio: "exclude"
    } as DisplayMediaStreamOptions);
  } catch (error) {
    if ((error as DOMException)?.name === "NotAllowedError") {
      throw new ScreenshotCancelledError();
    }
    throw error;
  }

  try {
    await waitForBlockingElements();
    return await grabFrame(stream);
  } finally {
    // Non-negotiable: a track left running leaves the tab flagged as being
    // shared, with the browser's recording indicator up, for the rest of the
    // session.
    stream.getTracks().forEach((track) => track.stop());
  }
}
